import React, { useState, useEffect, useCallback } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import SideNavigation from "./SideNavigation";
import NavigationHeader from "./NavigationHeader";
import Footer from "./Footer";
import OnboardingWizard from "../onboarding/OnboardingWizard";
import WelcomeTour, {
  shouldShowTour,
  markTourComplete,
} from "../onboarding/WelcomeTour";
import HelpPanel from "../help/HelpPanel";
import Icon from "../AppIcon";

const SIDEBAR_COLLAPSED_KEY = "rocket_tronc_sidebar_collapsed";

// Pages that regular employees (User role) can access
const EMPLOYEE_ALLOWED_PATHS = [
  "/my-tronc",
  "/my-clock-records",
  "/settings/profile",
  "/policy",
];

// Pages that don't need location/tenant context to render
const CONTEXT_FREE_PATHS = [
  "/settings/profile",
  "/settings/tenants",
  "/system/integrations",
  "/system/error-logs",
  "/system/scheduled-emails",
];

const AppLayout = () => {
  const { isAuthenticated, loading, user, isTenantAdmin, isGlobalAdmin } = useAuth();
  const { currentTenant, loading: tenantLoading, selectedTenant } = useTenantContext();
  const { currentLocation, loading: locationLoading, hasLocations } = useLocationContext();
  const { isTrial, trialDaysRemaining, isFreeTier } = useSubscription();
  const location = useLocation();
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === "true";
  });

  // Onboarding & tour state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Check if user is a regular employee
  const isRegularEmployee = user?.role === "User";

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Determine if onboarding wizard should show
  useEffect(() => {
    if (
      !loading &&
      isAuthenticated &&
      isTenantAdmin &&
      user?.role !== "GlobalAdmin" &&
      currentTenant &&
      currentTenant.hasCompletedOnboarding === false
    ) {
      setShowOnboarding(true);
    }
  }, [loading, isAuthenticated, isTenantAdmin, user, currentTenant]);

  // Show tour after onboarding completes (if not already seen)
  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    if (shouldShowTour()) {
      setShowTour(true);
    }
  }, []);

  const handleOnboardingSkip = useCallback(() => {
    setShowOnboarding(false);
    if (shouldShowTour()) {
      setShowTour(true);
    }
  }, []);

  const handleTourComplete = useCallback(() => {
    setShowTour(false);
  }, []);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // For regular employees, check if they're trying to access allowed pages
  if (isRegularEmployee) {
    const isAllowedPath = EMPLOYEE_ALLOWED_PATHS.some(
      (path) => location.pathname === path || location.pathname.startsWith(path + "/")
    );

    if (location.pathname === "/" || location.pathname === "/dashboard-overview") {
      return <Navigate to="/my-tronc" replace />;
    }

    if (!isAllowedPath) {
      return <Navigate to="/my-tronc" replace />;
    }
  }

  // Determine if the current page needs context to be ready
  const isContextFreePage = CONTEXT_FREE_PATHS.some(
 (path) => location.pathname === path || location.pathname.startsWith(path + "/")
  );

  // Context readiness check: tenant must be loaded for non-global admins,
  // or selected for global admins. Location must be loaded.
  // Employee pages (my-tronc) don't need location context.
  const tenantReady = isRegularEmployee || isContextFreePage || currentTenant != null;
  const locationReady = isRegularEmployee || isContextFreePage || !locationLoading;
  const contextReady = tenantReady && locationReady;

  // Main content padding matches the collapsed sidebar width
  const sidebarWidth = sidebarCollapsed ? "lg:pl-16" : "lg:pl-64";

  const handleToggleSidebar = () => {
    setSideNavOpen(!sideNavOpen);
  };

  return (
    <div className="min-h-screen bg-background">
   {/* Onboarding Wizard */}
      {showOnboarding && currentTenant && (
    <OnboardingWizard
       tenant={currentTenant}
          user={user}
    onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
     />
      )}

      {/* Welcome Tour */}
      {showTour && <WelcomeTour onComplete={handleTourComplete} />}

      {/* Side Navigation - always visible so user can select context */}
      <SideNavigation
   isOpen={sideNavOpen}
        onClose={() => setSideNavOpen(false)}
        isCollapsed={sidebarCollapsed}
    onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarWidth}`}>
        {/* Status Bar at the top */}
        <NavigationHeader
          isCollapsed={sidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
        />

      {/* Page Content - gated behind context readiness */}
     <main className="min-h-screen pt-14 pb-16">
          {/* Trial / Free tier banner */}
          {isTrial && trialDaysRemaining !== null && trialDaysRemaining <= 3 && (
            <div className={`mx-4 mt-2 mb-0 px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${trialDaysRemaining <= 1 ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
              <span>{trialDaysRemaining <= 1 ? '⚠️' : '⏰'}</span>
              <span className="font-medium">
                {trialDaysRemaining > 0
                  ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} left in your free trial.`
                  : 'Your free trial has expired.'}
              </span>
              <a href="/settings/subscription" className="ml-auto text-blue-600 hover:underline font-medium whitespace-nowrap">
                {isFreeTier ? 'Upgrade to Pro →' : 'View plan →'}
              </a>
            </div>
          )}
          {contextReady ? (
     <Outlet />
      ) : (
   <div className="flex items-center justify-center min-h-[60vh]">
   <div className="text-center space-y-4">
       <div className="spinner mx-auto" />
   <p className="text-muted-foreground text-sm">
      {tenantLoading ? "Loading workspace..." : locationLoading ? "Loading locations..." : "Preparing your view..."}
    </p>
  </div>
     </div>
   )}
        </main>
      </div>

      {/* Fixed Footer */}
      <Footer />

      {/* Help FAB button */}
      {!showOnboarding && !showTour && (
        <button
    onClick={() => setShowHelp(true)}
        className="fixed bottom-20 right-6 z-[1000] w-12 h-12 rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center transition-all"
          title="Help & Support"
        >
          <Icon name="HelpCircle" size={22} />
        </button>
   )}

      {/* Help Panel */}
      <HelpPanel isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default AppLayout;
